import { NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, signal } from '@angular/core';
import { faInfo } from '@fortawesome/free-solid-svg-icons';

import { unique } from '../../../common/utils/unique';
import { StarRatingComponent } from '../../../shared/components/star-rating/star-rating.component';
import {
    StickyAnchorContainerDirective,
} from '../../../shared/directives/sticky-anchor/sticky-anchor-container.directive';
import { StickyAnchorDirective } from '../../../shared/directives/sticky-anchor/sticky-anchor.directive';
import { GetProductDetailQuery, ProductPreviewFragment } from '../../common/gql/graphql';
import { AssetGalleryComponent, AssetWithDimensions } from '../asset-gallery/asset-gallery.component';
import { BrandLabelComponent } from '../brand-label/brand-label.component';
import { LargeLetterLabelComponent } from '../large-letter-label/large-letter-label.component';
import { OfferLabelComponent } from '../offer-label/offer-label.component';
import { ProductAccordionComponent } from '../product-accordion/product-accordion.component';
import { TabbedDescriptionComponent } from '../tabbed-description/tabbed-description.component';
import { VariantSelectorDefaultComponent } from '../variant-selector-default/variant-selector-default.component';

@Component({
    selector: 'kb-product-page-default',
    templateUrl: './product-page-default.component.html',
    styleUrls: ['./product-page-default.component.scss'],
    standalone: true,
    imports: [
        StickyAnchorContainerDirective,
        AssetGalleryComponent,
        BrandLabelComponent,
        StarRatingComponent,
        TabbedDescriptionComponent,
        StickyAnchorDirective,
        VariantSelectorDefaultComponent,
        OfferLabelComponent,
        NgIf,
        ProductAccordionComponent,
        LargeLetterLabelComponent,
    ],
})
export class ProductPageDefaultComponent implements OnChanges {
    @Input() productPreview: ProductPreviewFragment;
    @Input() product: NonNullable<GetProductDetailQuery['product']> | null;
    @Output() addToCartClick = new EventEmitter<{ productVariantId: string; quantity: number }>();
    @Output() updateOrderLineClick = new EventEmitter<{ orderLineId: string; quantity: number }>();
    @Output() addToWishlistClick = new EventEmitter<{ productVariantId: string; wishlistId?: string }>();
    allAssets: AssetWithDimensions[];
    selectedAssetId: string | undefined;
    selectedOptions: { [groupId: string]: string } = {};
    selectedVariant = signal<NonNullable<GetProductDetailQuery['product']>['variants'][number] | undefined>(undefined);
    qty = 1;
    inlineKeyFeatures: string | undefined;
    tabbedKeyFeatures: string | undefined;

    ngOnChanges() {
        for (const group of this.product?.optionGroups ?? []) {
            this.selectedOptions[group.id] = group.options[0].id;
        }
        if (this.productPreview && this.product) {
            const keyFeatures = this.productPreview.customFields?.keyFeatures ?? undefined;
            const optionGroupSizes = this.product.optionGroups.map((og) => og.options.length);
            const largestOptionGroup = Math.max(0, ...optionGroupSizes);
            const displayKeyFeaturesInline = largestOptionGroup < 10;
            this.inlineKeyFeatures = displayKeyFeaturesInline ? keyFeatures : undefined;
            this.tabbedKeyFeatures = displayKeyFeaturesInline ? undefined : keyFeatures;
            this.allAssets = [...this.product.assets];
            this.selectedAssetId = this.product?.featuredAsset?.id;
        }
    }

    setSelectedVariant(variantId: string) {
        const variant = this.product?.variants.find((v) => v.id === variantId);
        if (variant) {
            this.selectedVariant.set(variant);
            const variantAssets = variant.assets
                .slice()
                .sort((a, b) => (a.id === variant.featuredAsset?.id ? -1 : 1));
            if (variantAssets.length) {
                this.allAssets = unique([...(this.product?.assets ?? []), ...variantAssets], 'id');
                this.selectedAssetId = variantAssets[0].id;
            } else {
                this.allAssets = [...(this.product?.assets ?? [])];
                this.selectedAssetId = this.allAssets[0]?.id;
            }
        }
    }

    protected readonly info = faInfo;
}
